import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Button from '@atlaskit/button/button';
import Image from '@atlaskit/image/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';
import { CoreInvitesSource } from '@confluence/core-invites-provider/entry-points/constants.ts';

import FeedIllustrationLight from '../common/images/FeedIllustrationLight.svg';
import FeedIllustrationDark from '../common/images/FeedIllustrationDark.svg';
import FeedSkeletonLight from '../common/images/FeedSkeletonLight.svg';
import FeedSkeletonDark from '../common/images/FeedSkeletonDark.svg';

import PostFollowingLight from './images/PostFollowingLight.svg';
import PostFollowingDark from './images/PostFollowingDark.svg';
import { useFollowingFeedInviteDecision } from './useFollowingFeedInviteDecision.ts';

const messages = defineMessages({
	noTeamTitle: {
		id: 'feed.end-of-following.banner.no-team.title',
		defaultMessage: 'Nothing from your team yet.',
		description:
			'Bold leading text shown in the Following feed empty-state banner when there is no team activity yet.',
	},
	inviteDescription: {
		id: 'feed.end-of-following.banner.invite.description',
		defaultMessage: 'Invite your teammates to see their updates and trending content.',
		description:
			'Supporting text shown in the Following feed empty-state banner for founding admins who can invite teammates.',
	},
	caughtUpTitle: {
		id: 'feed.end-of-following.banner.caught-up.title',
		defaultMessage: "You're all caught up!",
		description:
			'Bold leading text shown in the Following feed empty-state banner when there are no new updates.',
	},
	caughtUpDescription: {
		id: 'feed.end-of-following.banner.caught-up.description',
		defaultMessage: 'Check back later or follow other teammates to see new updates.',
		description:
			'Supporting text shown in the Following feed empty-state banner when the user has no new updates.',
	},
	inviteCta: {
		id: 'feed.end-of-following.banner.invite.cta',
		defaultMessage: 'Invite teammates',
		description:
			'Button text in the Following feed empty-state banner that opens the invite teammates flow.',
	},
	followCta: {
		id: 'feed.end-of-following.banner.follow.cta',
		defaultMessage: 'Follow teammates',
		description:
			'Button text in the Following feed empty-state banner that opens feed settings for following teammates.',
	},
});

const bannerStyles = cssMap({
	banner: {
		borderRadius: token('radius.xlarge'),
		marginTop: token('space.100'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
	},
	content: {
		minWidth: 0,
	},
	text: {
		minWidth: 0,
	},
	// 32px square icon slot that centers the decorative banner mascot.
	// 32px == token('space.400'), but using this with cssMap causes eslint failures
	iconSlot: {
		alignItems: 'center',
		display: 'flex',
		flex: '0 0 auto',
		height: '32px',
		justifyContent: 'center',
		width: '32px',
	},
	ctaSkeleton: {
		backgroundColor: token('color.skeleton'),
		borderRadius: token('radius.small'),
		height: '32px',
		width: '136px',
	},
	skeletonImage: {
		display: 'block',
		height: 'auto',
		width: '100%',
	},
});

const FollowingFeedSkeletonCards = () => (
	<Box aria-hidden="true" testId="following-feed-skeleton-cards">
		<Image
			testId="following-feed-skeleton-card"
			src={FeedSkeletonLight}
			srcDark={FeedSkeletonDark}
			alt=""
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
			css={bannerStyles.skeletonImage}
		/>
	</Box>
);

type FollowingFeedEmptyStateBannerProps = {
	openActivityFilterModal?: () => void;
};

/**
 * Rendered by `EndOfFollowingFeed` when the Following feed has no visible
 * results and the `cc_mohiti_for_you_empty_states_exp` experiment is enabled.
 */
export const FollowingFeedEmptyStateBanner: React.FC<FollowingFeedEmptyStateBannerProps> = ({
	openActivityFilterModal,
}) => {
	const { openCoreInvites } = React.useContext(CoreInvitesContext);
	const { inviteDecisionLoading, shouldShowInviteCta } = useFollowingFeedInviteDecision({
		enabled: true,
	});

	const handleInviteTeammatesClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();

		openCoreInvites?.(CoreInvitesSource.PERSISTENT_INVITE_HOMESCREEN, {
			onModalClosed: () => null,
			onInviteCompleted: () => null,
		});
	};

	const bannerTitle = shouldShowInviteCta ? messages.noTeamTitle : messages.caughtUpTitle;
	const bannerDescription = shouldShowInviteCta
		? messages.inviteDescription
		: messages.caughtUpDescription;

	const getPrimaryAction = () => {
		switch (true) {
			case inviteDecisionLoading:
				return (
					<Box
						xcss={bannerStyles.ctaSkeleton}
						testId="following-feed-cta-skeleton"
						aria-hidden="true"
					/>
				);
			case shouldShowInviteCta:
				return (
					<Button onClick={handleInviteTeammatesClick}>
						<FormattedMessage {...messages.inviteCta} />
					</Button>
				);
			default:
				return (
					<Button onClick={openActivityFilterModal}>
						<FormattedMessage {...messages.followCta} />
					</Button>
				);
		}
	};

	return (
		<Stack space="space.300">
			<Box backgroundColor="elevation.surface.sunken" xcss={bannerStyles.banner}>
				<Inline alignBlock="center" spread="space-between" space="space.200">
					<Inline alignBlock="center" grow="fill" space="space.200" xcss={bannerStyles.content}>
						{shouldShowInviteCta ? (
							<Box
								xcss={bannerStyles.iconSlot}
								testId="following-feed-invite-icon"
								aria-hidden="true"
							>
								<Image
									src={FeedIllustrationLight}
									srcDark={FeedIllustrationDark}
									width="32"
									height="32"
									alt=""
								/>
							</Box>
						) : (
							<Box
								xcss={bannerStyles.iconSlot}
								testId="following-feed-caught-up-icon"
								aria-hidden="true"
							>
								<Image
									src={PostFollowingLight}
									srcDark={PostFollowingDark}
									width="32"
									height="32"
									alt=""
								/>
							</Box>
						)}
						<Box xcss={bannerStyles.text}>
							<Text as="p">
								<Text as="span" weight="bold">
									<FormattedMessage {...bannerTitle} />
								</Text>{' '}
								<FormattedMessage {...bannerDescription} />
							</Text>
						</Box>
					</Inline>
					{getPrimaryAction()}
				</Inline>
			</Box>
			<FollowingFeedSkeletonCards />
		</Stack>
	);
};
